import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { App, Button, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ApiClientError } from '@/lib/api-client';
import { authApi } from '../auth.api';
import { useAuth } from '../auth-context';
import { loginSchema, twoFactorSchema, type LoginForm, type TwoFactorForm } from '../auth.schemas';

export function LoginPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { setUser } = useAuth();
  const [challengeToken, setChallengeToken] = useState<string | null>(null);

  // After auth, honor a same-origin ?redirect target (e.g. accepting an invite).
  const goToDestination = () => {
    const redirect = new URLSearchParams(window.location.search).get('redirect');
    const target = redirect ? decodeURIComponent(redirect) : null;
    if (target && target.startsWith('/')) window.location.assign(target);
    else void navigate({ to: '/' });
  };

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const otpForm = useForm<TwoFactorForm>({
    resolver: zodResolver(twoFactorSchema),
    defaultValues: { code: '' },
  });

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      if (data.twoFactorRequired) {
        setChallengeToken(data.challengeToken);
        message.info('Enter the code we emailed you.');
      } else {
        setUser(data.user);
        goToDestination();
      }
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.body.code === 'AUTH_EMAIL_NOT_VERIFIED') {
        message.warning('Please verify your email before signing in.');
      } else {
        message.error(error instanceof Error ? error.message : 'Login failed');
      }
    },
  });

  const otpMutation = useMutation({
    mutationFn: (form: TwoFactorForm) =>
      authApi.verifyTwoFactor({ challengeToken: challengeToken ?? '', code: form.code }),
    onSuccess: (data) => {
      setUser(data.user);
      goToDestination();
    },
    onError: (error) => message.error(error instanceof Error ? error.message : 'Invalid code'),
  });

  if (challengeToken) {
    return (
      <Form layout="vertical" onFinish={otpForm.handleSubmit((v) => otpMutation.mutate(v))}>
        <Typography.Paragraph type="secondary">
          We sent a 6-digit verification code to your email.
        </Typography.Paragraph>
        <Controller
          control={otpForm.control}
          name="code"
          render={({ field, fieldState }) => (
            <Form.Item
              label="Verification code"
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} inputMode="numeric" maxLength={6} placeholder="123456" autoFocus />
            </Form.Item>
          )}
        />
        <Button type="primary" htmlType="submit" block loading={otpMutation.isPending}>
          Verify
        </Button>
        <Button type="link" block onClick={() => setChallengeToken(null)}>
          Back to sign in
        </Button>
      </Form>
    );
  }

  return (
    <Form layout="vertical" onFinish={loginForm.handleSubmit((v) => loginMutation.mutate(v))}>
      <Controller
        control={loginForm.control}
        name="email"
        render={({ field, fieldState }) => (
          <Form.Item
            label="Email"
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input {...field} type="email" autoComplete="email" placeholder="you@example.com" />
          </Form.Item>
        )}
      />
      <Controller
        control={loginForm.control}
        name="password"
        render={({ field, fieldState }) => (
          <Form.Item
            label="Password"
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input.Password {...field} autoComplete="current-password" placeholder="••••••••" />
          </Form.Item>
        )}
      />
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Link to="/forgot-password">Forgot password?</Link>
      </div>
      <Button type="primary" htmlType="submit" block loading={loginMutation.isPending}>
        Sign in
      </Button>
    </Form>
  );
}
